/**
 * Client helpers for visitor privacy gate.
 */

import {
  appendGeoSimToSearchParams,
  hasActiveGeoSim,
  readGeoSimSession,
} from "@/lib/geoSim/session";

export type VisitorPrivacyApi = {
  ok: boolean;
  ip: string | null;
  lookupIp: string | null;
  mode: "live" | "clean" | "anonymous" | "hosting";
  privacy: {
    isAnonymous: boolean | null;
    isVpn: boolean | null;
    isTor: boolean | null;
    isProxy: boolean | null;
    isHosting: boolean | null;
  };
  blocked: boolean;
  blockReason: string | null;
  allowlisted: boolean;
  cleanBypass: boolean;
  geoSimAuthorized: boolean;
};

/**
 * Conversion / checkout / thank-you surfaces — never show privacy overlay.
 * Belt-and-suspenders with server fail-open: even if snapshot.blocked is true
 * (stale deploy, QA leftover), CTAs and lead capture stay reachable.
 */
const PRIVACY_GATE_BYPASS_PATH_PREFIXES = [
  // Premium / exclusive invite funnels
  "/invitacion-exclusive",
  "/invitacion-exclusiva",
  "/invitacion-premium",
  "/invitacion-exclusiva-mux",
  "/invitacion-miembro",
  "/invitacion-mejores-amigos",
  "/invitacion-privada",
  "/invitacion",
  "/premium-invite",
  "/exclusive-invitation",
  "/exclusive-invite",
  "/exclusive-ar",
  "/exclusive",
  "/acceso",
  "/unete",
  "/vip",
  "/membresia-vip",
  "/membresia-telegram",
  "/grupo-privado",
  "/ar/invitacion",
  "/ar/invite",
  // Video landings with CTAs
  "/video-landing",
  "/video-vip",
  "/sorprendeme",
  "/sorpresa",
  // Checkout + pay
  "/checkout-channel-invite",
  "/checkout-ar-channel-invite",
  "/checkout-channel-invite-router",
  "/pagar",
  // Post-purchase / recovery (must never wall thank-you Telegram entry)
  "/muchas-gracias",
  "/gracias-por-tu-compra",
  "/gracias-por-tu-compra-latam",
  "/gracias",
  "/thank-you",
  "/premium",
] as const;

/** True when this path must never be blocked by the privacy gate (leads/conversion). */
export function isPrivacyGateBypassPath(pathname: string | null | undefined): boolean {
  const p = (pathname || "/").split("?")[0].toLowerCase();
  // Home DualCTA is the primary paid landing — never cover CTAs
  if (p === "/" || p === "") return true;
  return PRIVACY_GATE_BYPASS_PATH_PREFIXES.some(
    (prefix) => p === prefix || p.startsWith(`${prefix}/`),
  );
}

/** Build query for snapshot including active geo_sim params. */
export function buildVisitorPrivacyQuery(): string {
  const params = new URLSearchParams();
  appendGeoSimToSearchParams(params);
  // Ensure clean mode is explicit when session is clean
  const session = readGeoSimSession();
  if (session && !params.get("simulate_vpn")) {
    params.set("simulate_vpn", session.simulateVpn || "clean");
  }
  if (hasActiveGeoSim() && !params.get("geo_sim")) {
    params.set("geo_sim", "1");
  }
  return params.toString();
}

export async function fetchVisitorPrivacySnapshot(
  signal?: AbortSignal,
): Promise<VisitorPrivacyApi | null> {
  try {
    const qs = buildVisitorPrivacyQuery();
    const url = qs
      ? `/api/visitor-privacy-snapshot?${qs}`
      : "/api/visitor-privacy-snapshot";
    const res = await fetch(url, { method: "GET", cache: "no-store", signal });
    if (!res.ok) return null;
    return (await res.json()) as VisitorPrivacyApi;
  } catch {
    return null;
  }
}

/**
 * SPA effective block — LEAD-SAFE fail-open:
 * - super_admin → never block
 * - conversion paths (home DualCTA, invites, checkout, thank-you) → never block
 * - geo_sim + Clean → never block
 * - live mode (or missing snapshot) → never block (IPLocate errors must not kill CTAs)
 * - only geo_sim QA modes anonymous|hosting with server blocked=true may overlay
 */
export function shouldBlockPrivacyGate(opts: {
  snapshot: VisitorPrivacyApi | null;
  isSuperAdmin: boolean;
  /** Current SPA path — conversion routes always bypass. */
  pathname?: string | null;
}): boolean {
  if (opts.isSuperAdmin) return false;
  if (isPrivacyGateBypassPath(opts.pathname)) return false;
  const session = readGeoSimSession();
  if (session && (session.simulateVpn || "clean") === "clean") return false;
  if (opts.snapshot?.cleanBypass) return false;
  if (opts.snapshot?.allowlisted) return false;

  // No snapshot / fetch failed → fail open (do not cover DualCTA while loading errors)
  if (!opts.snapshot) return false;

  // Live traffic: never overlay, even if a stale server still sends blocked=true.
  // (Do not special-case privacy.isVpn here: QA mode "anonymous" forces isVpn=true.)
  const mode = opts.snapshot.mode || "live";
  if (mode === "live" || mode === "clean") return false;

  // Only explicit QA sim modes may show the overlay (non-conversion paths)
  if (mode !== "anonymous" && mode !== "hosting") return false;
  return opts.snapshot.blocked === true;
}
